import { Popup } from 'components/common/popup/Popup';
import React, { Suspense, useEffect, useState } from 'react';
import { FormConfirmState } from 'components/common/form/act/useActOnConfirm';
import useBundleTranslation from 'i18n';
import { ConfirmationContentProps, ConfirmationComponentsMap } from 'components/common/form/confirmation/index';
import Text from 'components/common/form/confirmation/content/Text';
import LoadingPlaceholder from 'components/common/loading-placeholder/LoadingPlaceholder';

export default function FormConfirmation({
    formConfirmState,
    handleConfirm,
    handleCancel,
}: {
    formConfirmState: Array<FormConfirmState>;
    handleConfirm: (key: string) => void;
    handleCancel: () => void;
}) {
    const { t } = useBundleTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [actualConf, setActualConf] = useState<FormConfirmState>();

    useEffect(() => {
        const next = formConfirmState.find((c) => !c.confirmed);
        setActualConf(next);
    }, [formConfirmState]);

    useEffect(() => {
        setIsOpen(actualConf !== undefined);
    }, [actualConf]);

    return (
        <Popup
            settings={{
                noButtons: true,
                title: t('confirmation'),
                textOK: t('ok'),
            }}
            open={isOpen}
            onHide={handleCancel}
        >
            <Suspense fallback={<LoadingPlaceholder />}>
                <ConfirmationComponent
                    onConfirm={() => {
                        if (!actualConf) {
                            return;
                        }
                        handleConfirm(actualConf.key);
                    }}
                    onCancel={handleCancel}
                    message={actualConf?.message}
                    childProps={actualConf?.props}
                />
            </Suspense>
        </Popup>
    );
}

function ConfirmationComponent({
    message,
    childProps,
    onConfirm,
    onCancel,
}: ConfirmationContentProps & { message?: string; childProps: any }) {
    if (!message) {
        return null;
    }
    if (message.startsWith('COMPONENT::')) {
        const componentName = message.replace('COMPONENT::', '');
        const Component = ConfirmationComponentsMap.get(componentName);
        if (Component) {
            return <Component {...childProps} onConfirm={onConfirm} onCancel={onCancel} />;
        }
    }
    return <Text onConfirm={onConfirm} onCancel={onCancel} message={message} />;
}
